import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { uiCopy } from '../copy'
import { cn } from '../lib/cn'
import { useUiCopy } from '../lib/ui-copy'
import { DialogActions } from './DialogActions'

const modalStack: symbol[] = []

/**
 * FF-AUDIT-20260914-210459-066: a dialog holding unsaved edits used to close
 * on Escape / an outside click and drop them without a word (or, guarded
 * with `disableEscape`, ignore the key without a word). One question,
 * asked on every close path — the primitive's own (Escape, overlay) and the
 * feature's Cancel button, which calls the same hook.
 */
export function useDiscardGuard(dirty: boolean): (close: () => void) => void {
  const c = useUiCopy(uiCopy)
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  return useCallback(
    (close: () => void) => {
      if (dirtyRef.current && !window.confirm(c.dialog.discardChanges)) return
      close()
    },
    [c.dialog.discardChanges],
  )
}

/** Tailwind `z-50`, the tier every page-level overlay in the app sits on. */
const BASE_Z_INDEX = 50

/**
 * The shared accessible modal primitive (MODULE_BLUEPRINT §10.5, WCAG 2.2):
 * focus moves into the panel on open, stays trapped (Tab cycles), Escape
 * closes (unless the dialog is guarding a destructive/in-progress action),
 * and focus returns to the trigger on close. Feature dialogs compose this
 * instead of hand-rolling fixed overlays.
 *
 * FF-QA-091: the panel is the scroll container, capped at 90 vh. A tall form
 * (the work-order completion dialog on a 1366×768 screen) used to scroll its
 * title away and hide its Cancel / Submit row below the fold with no hint
 * that more followed. The title/description now stay pinned at the top (a
 * border appears once the body has scrolled — the cue), and the action row
 * stays pinned at the bottom: pass it as `footer`, or wrap it in
 * `DialogActions` inside the body — both are the same sticky row.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  disableEscape = false,
  dirty = false,
  className,
  containerClassName,
  contentClassName,
  titleVisuallyHidden = false,
  unstyled = false,
  restoreFocusRef,
}: {
  open: boolean
  /** Called on Escape and overlay click. Render nothing when closed. */
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  /** FF-QA-091: the Cancel / Submit row, pinned to the panel's bottom edge
   * while the body scrolls. Equivalent to ending `children` with a
   * `DialogActions` row; use this when the buttons are not part of a form
   * inside the body. */
  footer?: ReactNode
  /** Set while an action is running — Escape and the overlay do nothing. */
  disableEscape?: boolean
  /** FF-AUDIT-066: unsaved edits — Escape and the overlay ask "Discard your unsaved changes?" before closing. */
  dirty?: boolean
  /** Explicit element to return focus to on close. Use this when the trigger
   * is unmounted by the same interaction that opens the dialog (e.g. a menu
   * item inside a dropdown that closes on select) — otherwise the auto-captured
   * trigger is detached on close and focus is lost to <body>. */
  restoreFocusRef?: RefObject<HTMLElement | null>
  /** Extra classes for the panel (e.g. max-w-2xl). */
  className?: string
  /** Extra classes for positioning the modal panel within the viewport. */
  containerClassName?: string
  /** Extra classes for the content wrapper below the title/description. */
  contentClassName?: string
  /** Keep the accessible name while visually hiding the heading. */
  titleVisuallyHidden?: boolean
  /** Let an existing surface supply its own border/background/padding during migration. */
  unstyled?: boolean
}) {
  const c = useUiCopy(uiCopy)
  const panelRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)
  const modalIdRef = useRef(Symbol('floorfile-dialog'))
  // FF-AUDIT-20260914-210459-084: the overlay's z-index is the dialog's
  // position in the open order, captured the moment it opens. Every wrapper
  // used to share `z-50`, so the LATER sibling in DOM order painted on top —
  // the Part 11 sign dialog (rendered by `useEsign` at the top of a page) sat
  // underneath the "Complete audit" / "Close this CAR?" dialog that had just
  // asked for it, while that visible dialog was inert. Stacking by open order
  // makes the newest dialog the top one wherever it is rendered.
  const depthRef = useRef(0)
  const requestClose = useDiscardGuard(dirty)
  const onCloseRef = useRef(() => requestClose(onClose))
  const disableEscapeRef = useRef(disableEscape)
  // Mirror the explicit restore target into a ref so the unmount cleanup reads
  // the latest value without re-subscribing the effect (matches onCloseRef).
  const explicitRestoreRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descriptionId = useId()
  // FF-QA-091: the pinned header shows its border only once the body has
  // scrolled under it — a static border would read as a divider, not a cue.
  const [scrolled, setScrolled] = useState(false)

  onCloseRef.current = () => requestClose(onClose)
  disableEscapeRef.current = disableEscape
  explicitRestoreRef.current = restoreFocusRef?.current ?? null
  // Capture synchronously before React commits descendants with `autoFocus`;
  // a passive effect would see the modal input instead of the opener.
  if (open && !wasOpenRef.current) {
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    depthRef.current = modalStack.length
  }
  wasOpenRef.current = open

  const focusables = useCallback((): HTMLElement[] => {
    if (!panelRef.current) return []
    return Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    setScrolled(false)
    const modalId = modalIdRef.current
    modalStack.push(modalId)
    const hiddenSiblings: Array<{ element: HTMLElement; inert: boolean; ariaHidden: string | null }> = []
    let branch: HTMLElement | null = overlayRef.current
    while (branch && branch !== document.body) {
      const parent: HTMLElement | null = branch.parentElement
      if (!parent) break
      for (const sibling of Array.from(parent.children)) {
        if (!(sibling instanceof HTMLElement) || sibling === branch) continue
        hiddenSiblings.push({ element: sibling, inert: sibling.inert, ariaHidden: sibling.getAttribute('aria-hidden') })
        sibling.inert = true
        sibling.setAttribute('aria-hidden', 'true')
      }
      branch = parent
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const first = focusables()[0] ?? panelRef.current
    first?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (modalStack.at(-1) !== modalId) return
      if (event.key === 'Escape' && !disableEscapeRef.current) {
        // A focused descendant that owns an OPEN popup takes Escape first
        // (WAI-ARIA combobox pattern). This listener is on `document` in the
        // CAPTURE phase and calls `stopImmediatePropagation`, so without this
        // check it swallowed the key before the control ever saw it: the only
        // way to dismiss a suggestion list was to throw away the half-filled
        // form behind it. Yield and let the control close its own popup — we
        // neither preventDefault nor stop propagation on this path.
        const target = event.target as HTMLElement | null
        if (target?.closest('[role="combobox"][aria-expanded="true"]')) return
        event.preventDefault()
        event.stopImmediatePropagation()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        return
      }
      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault()
        lastItem?.focus()
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault()
        firstItem?.focus()
      } else if (panelRef.current && !panelRef.current.contains(document.activeElement)) {
        event.preventDefault()
        firstItem?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const stackIndex = modalStack.lastIndexOf(modalId)
      if (stackIndex >= 0) modalStack.splice(stackIndex, 1)
      for (const { element, inert, ariaHidden } of hiddenSiblings) {
        element.inert = inert
        if (ariaHidden === null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', ariaHidden)
      }
      document.body.style.overflow = previousOverflow
      // Return focus, preferring an explicit target, then the auto-captured
      // trigger — but never a disconnected node (which would drop focus to
      // <body>). The explicit ref covers triggers that unmount on select.
      const explicit = explicitRestoreRef.current
      const captured = restoreRef.current
      const target =
        explicit?.isConnected === true
          ? explicit
          : captured?.isConnected === true
            ? captured
            : null
      target?.focus()
    }
  }, [open, focusables])

  if (!open) return null

  // The pinned header band exists only when it has something to show: a
  // visually-hidden title with no description (the drawers, the lightbox)
  // keeps the bare sr-only heading and no band.
  const pinnedHeader = !unstyled && (!titleVisuallyHidden || Boolean(description))
  const heading = (
    <h2 id={titleId} className={cn('text-lg font-semibold', titleVisuallyHidden && 'sr-only')}>
      {title}
    </h2>
  )
  const summary = description && (
    <p id={descriptionId} className="mt-1 text-sm text-muted-foreground">
      {description}
    </p>
  )

  // FF-AUDIT-20260921-210633-020: rendered in place, the overlay's
  // `position: fixed` is relative to the nearest ancestor with a transform,
  // filter or backdrop-filter — the RecordPage header (`backdrop-blur`) is
  // one, so a dialog opened from a header action ("Link a quality issue…"
  // on an incident) was centred inside a 111-px-tall box, its title and
  // first field above the viewport. Portalled to <body>, the overlay's only
  // containing block is the viewport. React events still bubble to the
  // owner, the sibling-inert walk above starts at <body> and covers the app
  // root, and stacking by open order is unchanged.
  return createPortal(
    <div
      ref={overlayRef}
      className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', containerClassName)}
      style={{ zIndex: BASE_Z_INDEX + depthRef.current }}
    >
      <button
        type="button"
        aria-label={c.dialog.close}
        tabIndex={-1}
        /* audit UI-18: `bg-foreground/40` inverted in dark mode — the scrim
           LIGHTENED the page behind the modal. `--overlay` is black in both. */
        className="absolute inset-0 cursor-default bg-overlay/40 dark:bg-overlay/60"
        onClick={() => {
          if (!disableEscape) requestClose(onClose)
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
        onScroll={(event) => setScrolled(event.currentTarget.scrollTop > 0)}
        className={cn(
          unstyled
            ? 'relative max-h-[90vh] w-full overflow-y-auto outline-none'
            : 'relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-4 text-card-foreground shadow-lg outline-none sm:p-6',
          className,
        )}
      >
        {pinnedHeader ? (
          // Negative margins cancel the panel padding so the band spans edge
          // to edge (the same arithmetic as DialogActions at the bottom). The
          // sticky edge must be offset by the same padding (`-top-4`,
          // `sm:-top-6`): a sticky element is held inside the scroll
          // container's PADDING box, so `top-0` pushed the band back down by
          // the padding at rest and it covered the first 16–24 px of the body
          // — in a short confirm dialog, its buttons (unclickable where they
          // were covered; found by the e2e suite, 2026-09-27).
          <div
            className={cn(
              'sticky -top-4 z-10 -mx-4 -mt-4 border-b bg-card px-4 pb-3 pt-4 sm:-top-6 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6',
              scrolled ? 'border-border' : 'border-transparent',
            )}
          >
            {heading}
            {summary}
          </div>
        ) : (
          <>
            {heading}
            {summary}
          </>
        )}
        <div className={cn(pinnedHeader ? 'mt-1' : 'mt-4', contentClassName)}>{children}</div>
        {footer && <DialogActions>{footer}</DialogActions>}
      </div>
    </div>,
    document.body,
  )
}
